import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from '@tanstack/react-router'
import { $api } from '../../api/queryClient'
import type { AgentRun } from '../../api/schema'
import { useMyOrganizations } from '../../api/multiOrg'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useLocale } from '../../contexts/LocaleContext'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/skeleton'
import { EmptyState } from '../ui/empty-state'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { AGENT_RUN_STATUS, displayStatus, toneVariant } from './agent-run-status'
import { formatDuration, useTargetName } from './AgentRunPage'
import { useAgentMissions } from './agent-runs-api'

export const RUN_STATUS_FILTERS = ['active', 'needs_decision', 'finished', 'failed'] as const
export const RUN_TYPE_FILTERS = ['asset', 'import', 'assistant_thread'] as const

export interface AgentRunsListSearch {
    status?: (typeof RUN_STATUS_FILTERS)[number]
    type?: (typeof RUN_TYPE_FILTERS)[number]
    page?: number
}

export const AGENT_RUNS_PAGE_SIZE = 50

/** The API query behind a chip. "needs a decision" is filtered by the server, so the pager stays exact. */
const STATUS_QUERY: Record<(typeof RUN_STATUS_FILTERS)[number], { status?: string; needs_decision?: boolean }> = {
    active: { status: 'queued,running' },
    needs_decision: { needs_decision: true },
    finished: { status: 'succeeded,cancelled' },
    failed: { status: 'failed,timed_out,interrupted' },
}

/** The API lists assistant runs for organization owners and admins only. */
const ASSISTANT_ROLES = ['owner', 'admin']

function RunRow({ org, run, missionTitle }: { org: string; run: AgentRun; missionTitle: string }) {
    const { t } = useTranslation('agent-runs')
    const { locale } = useLocale()
    const target = useTargetName(org, run)
    const cfg = AGENT_RUN_STATUS[displayStatus(run)]
    const end = run.finished_at ? new Date(run.finished_at).getTime() : Date.now()
    const duration = run.started_at ? formatDuration(end - new Date(run.started_at).getTime()) : '—'

    return (
        <TableRow data-testid={`agent-runs-row-${run.uid}`}>
            <TableCell className="font-medium">
                <Link
                    to="/$org/agent-runs/$runUid"
                    params={{ org, runUid: run.uid }}
                    search={{}}
                    className="hover:underline"
                    data-testid={`agent-runs-row-link-${run.uid}`}
                >
                    {missionTitle}
                </Link>
            </TableCell>
            <TableCell data-testid="agent-runs-row-target">
                {target.path ? (
                    <Link to={target.path} className="hover:underline">{target.name ?? '…'}</Link>
                ) : (
                    target.name ?? '—'
                )}
            </TableCell>
            <TableCell>
                <Badge variant={toneVariant(cfg.tone)} className="gap-1" data-testid="agent-runs-row-status">
                    <AppIcon path={cfg.icon} size="xs" />
                    {t(cfg.labelKey)}
                </Badge>
            </TableCell>
            <TableCell>{run.requested_by?.name ?? '—'}</TableCell>
            <TableCell className="whitespace-nowrap">{new Date(run.created_at).toLocaleString(locale)}</TableCell>
            <TableCell className="whitespace-nowrap">{duration}</TableCell>
            <TableCell className="tabular-nums" data-testid="agent-runs-row-pending">
                {run.pending_proposals.total > 0 ? run.pending_proposals.total : '—'}
            </TableCell>
        </TableRow>
    )
}

export function AgentRunsListPage({ org, search }: { org: string; search: AgentRunsListSearch }) {
    const { t } = useTranslation('agent-runs')
    const navigate = useNavigate()
    const page = search.page ?? 1
    const offset = (page - 1) * AGENT_RUNS_PAGE_SIZE

    useBreadcrumbs(
        useMemo(
            () => [{ title: t('common:nav.home'), path: `/${org}` }, { title: t('list.breadcrumb') }],
            [org, t],
        ),
    )

    const { data: missions } = useAgentMissions(org)
    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/agent-runs', {
        params: {
            path: { orgCode: org },
            query: {
                limit: AGENT_RUNS_PAGE_SIZE,
                offset,
                ...(search.status ? STATUS_QUERY[search.status] : {}),
                ...(search.type ? { target_entity_type: search.type } : {}),
            },
        },
    })
    const total = data?.pagination.total ?? 0
    const runs = data?.data ?? []
    const { data: mine } = useMyOrganizations()
    const seesAssistant = ASSISTANT_ROLES.includes(mine?.data?.find((o) => o.code === org)?.role?.code ?? '')
    const typeFilters = RUN_TYPE_FILTERS.filter((ty) => ty !== 'assistant_thread' || seesAssistant)

    const go = (next: AgentRunsListSearch) =>
        navigate({ to: '/$org/agent-runs', params: { org }, search: next })
    const title = (run: AgentRun) => {
        const mission = missions?.data?.find((m) => m.code === run.mission_code)
        return mission ? t(mission.title_key, { defaultValue: mission.code }) : run.mission_code
    }
    const chip = (testid: string, label: string, active: boolean, onClick: () => void) => (
        <Button
            key={testid}
            size="sm"
            variant={active ? 'default' : 'outline'}
            onClick={onClick}
            aria-pressed={active}
            data-testid={testid}
        >
            {label}
        </Button>
    )

    return (
        <div className="space-y-6" data-testid="agent-runs-list-page">
            <PageHeader icon={SECTION_ICONS.agentRun} title={t('list.title')} description={t('list.description')} />

            <div className="space-y-2">
                <div className="flex flex-wrap gap-2" data-testid="agent-runs-status-chips">
                    {chip('agent-runs-status-all', t('list.status_filter.all'), !search.status, () =>
                        go({ type: search.type }))}
                    {RUN_STATUS_FILTERS.map((s) =>
                        chip(`agent-runs-status-${s}`, t(`list.status_filter.${s}`), search.status === s, () =>
                            go({ status: s, type: search.type })))}
                </div>
                <div className="flex flex-wrap gap-2" data-testid="agent-runs-type-chips">
                    {chip('agent-runs-type-all', t('list.type_filter.all'), !search.type, () =>
                        go({ status: search.status }))}
                    {typeFilters.map((ty) =>
                        chip(`agent-runs-type-${ty}`, t(`list.type_filter.${ty}`), search.type === ty, () =>
                            go({ status: search.status, type: ty })))}
                </div>
            </div>

            {isLoading ? (
                <Skeleton className="h-40 w-full" />
            ) : runs.length === 0 ? (
                <EmptyState
                    variant="panel"
                    title={search.status || search.type ? t('list.empty_filtered') : t('list.empty')}
                    data-testid="agent-runs-empty"
                />
            ) : (
                <Table data-testid="agent-runs-table">
                    <TableHeader>
                        <TableRow>
                            {(['mission', 'target', 'status', 'requested_by', 'created', 'duration', 'pending'] as const).map((c) => (
                                <TableHead key={c}>{t(`list.columns.${c}`)}</TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {runs.map((run) => (
                            <RunRow key={run.uid} org={org} run={run} missionTitle={title(run)} />
                        ))}
                    </TableBody>
                </Table>
            )}

            {total > AGENT_RUNS_PAGE_SIZE && (
                <div className="flex items-center justify-between text-sm" data-testid="agent-runs-pager">
                    <span className="text-muted-foreground" data-testid="agent-runs-range">
                        {t('list.range', { from: offset + 1, to: Math.min(offset + AGENT_RUNS_PAGE_SIZE, total), total })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page <= 1}
                            onClick={() => go({ ...search, page: page - 1 })}
                            data-testid="agent-runs-prev"
                        >
                            {t('list.prev')}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={!data?.pagination.has_more}
                            onClick={() => go({ ...search, page: page + 1 })}
                            data-testid="agent-runs-next"
                        >
                            {t('list.next')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
